import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Textarea } from '@fluentui/react-components'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { ApplicationRoleItem } from '../ApplicationControl/AppRolesPanel'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useQueryClient } from '@tanstack/react-query'

type MessagesApplicationsApplicationRole = RestSchemas['Messages.Applications.ApplicationRole']
type MessagesApplicationsNewApplicationRole = RestSchemas['Messages.Applications.NewApplicationRole']

export type ApplicationRoleForm = {
  name: string
  description: string
}

type AddApplicationRoleFormProps = {
  onSubmit: () => void
  selectedAppRole?: ApplicationRoleItem
  appId?: number
}

export const AddApplicationRoleForm = (props: AddApplicationRoleFormProps) => {
  const queryClient = useQueryClient()
  const {
    postApplicationsApiUpdateApplicationRole,
    putApplicationsApiAddApplicationRole,
    getApplicationsApiListApplicationRoles,
  } = createRestClient()

  const { mutate: addAppRole, isPending: isAddingAppRole } = putApplicationsApiAddApplicationRole.useMutation()
  const { mutate: updateAppRole, isPending: isUpdatingAppRole } = postApplicationsApiUpdateApplicationRole.useMutation()

  const invalidateApplicationRoles = () => {
    getApplicationsApiListApplicationRoles.invalidateQueries(
      {
        parameters: {
          query: {
            applicationId: props.appId,
          },
        },
      },
      queryClient
    )
  }

  const {
    register,
    formState: { errors },
    // setError,
    reset,
    handleSubmit,
    // control
  } = useForm<ApplicationRoleForm>({
    defaultValues: props.selectedAppRole
      ? ({
          name: props.selectedAppRole.name.value,
          description: props.selectedAppRole.description.value,
        } as ApplicationRoleForm)
      : ({
          name: '',
          description: '',
        } as ApplicationRoleForm),
  })

  const onSubmit = (data: ApplicationRoleForm) => {
    if (props.selectedAppRole) {
      const updatedRole: MessagesApplicationsApplicationRole = {
        id: props.selectedAppRole!.id!,
        applicationId: props.selectedAppRole!.applicationId!,
        teamCount: 0,
        memberCount: 0,
        ...data,
      }

      updateAppRole(
        { body: updatedRole },
        {
          onSuccess: () => {
            invalidateApplicationRoles()
            toast.success('App role successfully updated')
            reset()
            props.onSubmit()
          },
          onError: () => {
            toast.error('Error updating app role')
          },
        }
      )
    } else {
      const newAppRole: MessagesApplicationsNewApplicationRole = {
        applicationId: props.appId!,
        ...data,
      }

      addAppRole(
        { body: newAppRole },
        {
          onSuccess: () => {
            invalidateApplicationRoles()
            toast.success('App role successfully added')
            reset()
            props.onSubmit()
          },
          onError: () => {
            toast.error('Error adding app role')
          },
        }
      )
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        <Field
          label="Name"
          required
          validationState={errors.name ? 'error' : 'none'}
          validationMessage={errors.name?.message}
        >
          <Input
            {...register('name', {
              required: 'Name is required',
              maxLength: 256,
            })}
          />
        </Field>
        <Field
          label="Description"
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Textarea {...register('description', { maxLength: 256 })} rows={4} />
        </Field>
      </div>
      <div className="form-action-container">
        <Button disabled={isAddingAppRole || isUpdatingAppRole} text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
